import * as React from 'react'

import { Box, Icon } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Tooltip from 'modules/core/components/Tooltip'
import i18n from 'modules/core/lib/i18n'

import BoxListItem from 'modules/reports/components/BoxListItem'
import Field from 'modules/reports/components/Editor/Field'
import Options from 'modules/reports/components/Editor/Field/Options'
import type { FieldType } from 'modules/reports/components/Editor/Field/types'
import { useValidationContext } from 'modules/reports/contexts/validationContext'
import type { AbilityType } from 'modules/reports/store/models/types'

import SortableList from './SortableList'

type ItemProps = {
  fieldType: FieldType
  fieldAbilityTypes?: AbilityType[]
  variables: unknown[]
  disabled: boolean
  loading: boolean
  onRemoveField: (fieldType: FieldType) => void
}

const Item = ({
  fieldType,
  fieldAbilityTypes,
  variables,
  disabled,
  loading,
  onRemoveField,
}: ItemProps) => {
  const notClickable = disabled || loading

  return (
    <BoxListItem
      sortable={!notClickable}
      label={fieldType.field?.attributes.label ?? ''}
      subtitle={fieldType.field?.attributes.category_label ?? ''}
      options={
        <Box gap="s4" flexDirection="row" alignItems="center">
          <Options fieldType={fieldType} types={fieldAbilityTypes!} variables={variables} />
          <Tooltip.Alt
            label={i18n.t(
              'custom_reports.fields.multi_projection_field.disabled_first_field_tooltip'
            )}
            attachment="bottom center"
            disabled={!disabled}
          >
            <Box
              cursor={notClickable ? undefined : 'pointer'}
              onClick={() => onRemoveField(fieldType)}
              padding="s4"
              justifyContent="center"
            >
              <Icon.Medium icon="delete" color={disabled ? 'grey400' : 'grey600'} />
            </Box>
          </Tooltip.Alt>
        </Box>
      }
    />
  )
}

type Props = {
  chartType?: string
  compactedFieldTypes: FieldType[]
  fieldAbilityTypes?: AbilityType[]
  disabled?: boolean
  variables: unknown[]
  onRemoveField: (fieldType: FieldType) => void
  onReorderFields?: (items: FieldType[]) => void
}

const FieldsList = ({
  chartType,
  compactedFieldTypes,
  fieldAbilityTypes,
  variables,
  onRemoveField,
  onReorderFields,
}: Props) => {
  const [validation] = useValidationContext()

  if (chartType === 'table' && onReorderFields) {
    return (
      <SortableList
        items={compactedFieldTypes}
        onSortEnd={onReorderFields}
        renderItem={(item, disabled, loading) => (
          <Item
            fieldType={item}
            fieldAbilityTypes={fieldAbilityTypes}
            variables={variables}
            disabled={disabled}
            loading={loading}
            onRemoveField={onRemoveField}
          />
        )}
      />
    )
  }

  return (
    <>
      {compactedFieldTypes.map((fieldType) => {
        const fieldPath = fieldType.form.get('path').value

        return (
          <Field
            key={fieldPath}
            variables={variables}
            fieldType={fieldType}
            onRemoveField={onRemoveField}
            types={fieldAbilityTypes}
            fieldDisabled={validation.errors.fields.includes(fieldPath)}
          />
        )
      })}
    </>
  )
}

export default observer(FieldsList)
